import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertCircle, AlertTriangle, Calendar, Clock, ExternalLink, Loader2, Search, TrendingUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import { getGmDashboard } from "@/lib/dashboardApi";
import { getOrCreateProposalPublicLink } from "@/lib/proposalsApi";
import { lookupUsersByRoles } from "@/lib/usersApi";
import { cn } from "@/lib/utils";
import { getUserColorToken, getUserInitials } from "@/lib/userIdentity";

type OwnerStatusRow = {
  ownerId: string | null;
  ownerName: string;
  statusBreakdown: Record<string, number>;
  totalProposals: number;
};

type BoardItem = {
  proposalId: string;
  caseNumber: string;
  title?: string | null;
  clientName?: string | null;
  status: string;
  priority?: string | null;
  updatedAt?: string | null;
  expiryDate?: string | null;
  statusAgeBusinessDays?: number | null;
  totalValue?: number | null;
  tripStartDate?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  staleFollowUp?: boolean | null;
  daysSinceLastFollowUp?: number | null;
};

type BoardColumn = {
  status: string;
  label: string;
  items: BoardItem[];
};

type Employee = {
  id: string;
  full_name?: string;
  profile_photo?: string | null;
};

interface GMMetrics {
  todayProposals: number;
  next7Days: number;

  ownerStatusRows: OwnerStatusRow[];
  boardColumns: BoardColumn[];

  avgCreationToSent: number;
  avgSentToApproved: number;
  slaCompliance: number;

  overdueProposals: number;
  rework: number;

  upcomingProposals: any[];
  pendingProposals: any[];
}

const GMDashboard = () => {
  const [metrics, setMetrics] = useState<GMMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewingProposalId, setPreviewingProposalId] = useState<string | null>(null);
  const [highlightUpcomingTrips, setHighlightUpcomingTrips] = useState(false);
  const upcomingTripsRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const scrollToUpcomingTrips = () => {
    upcomingTripsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightUpcomingTrips(true);
    window.setTimeout(() => setHighlightUpcomingTrips(false), 1600);
  };

  useEffect(() => {
    fetchMetrics();
    fetchEmployees();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await getGmDashboard();
      setMetrics({
        todayProposals: response.today_proposals || 0,
        next7Days: response.next7_days || 0,
        ownerStatusRows: (response.owner_status_rows || []).map((row) => ({
          ownerId: row.owner_id,
          ownerName: row.owner_name,
          statusBreakdown: row.status_breakdown || {},
          totalProposals: row.total_proposals || 0,
        })),
        boardColumns: (response.board_columns || []).map((column) => ({
          status: column.status,
          label: column.label,
          items: (column.items || []).map((item) => ({
            proposalId: item.proposal_id,
            caseNumber: item.case_number,
            title: item.title,
            clientName: item.client_name,
            status: item.status,
            priority: item.priority,
            updatedAt: item.updated_at,
            expiryDate: item.expiry_date,
            statusAgeBusinessDays: item.status_age_business_days,
            totalValue: item.total_value,
            tripStartDate: item.trip_start_date,
            ownerId: item.owner_id,
            ownerName: item.owner_name,
            staleFollowUp: item.stale_follow_up,
            daysSinceLastFollowUp: item.days_since_last_follow_up,
          })),
        })),
        avgCreationToSent: response.avg_creation_to_sent || 0,
        avgSentToApproved: response.avg_sent_to_approved || 0,
        slaCompliance: response.sla_compliance || 0,
        overdueProposals: response.overdue_proposals || 0,
        rework: response.rework || 0,
        upcomingProposals: response.upcoming_proposals || [],
        pendingProposals: response.pending_proposals || [],
      });
    } catch (error: any) {
      console.error("Error fetching GM metrics:", error);
      toast({
        title: "Error loading metrics",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await lookupUsersByRoles([
        "ceo",
        "general_manager",
        "sales_staff",
        "operations_staff",
        "finance_staff",
      ]);
      setEmployees((data as Employee[]) || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar owners",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const employeesById = useMemo(() => {
    return new Map(employees.map((employee) => [employee.id, employee]));
  }, [employees]);

  const ownerFilters = useMemo(() => {
    if (!metrics) return [];

    const ownerCounts = new Map<string, number>();
    const ownerNames = new Map<string, string>();

    for (const column of metrics.boardColumns) {
      for (const item of column.items) {
        const ownerId = item.ownerId || "unassigned";
        ownerCounts.set(ownerId, (ownerCounts.get(ownerId) || 0) + 1);
        if (item.ownerName) {
          ownerNames.set(ownerId, item.ownerName);
        }
      }
    }

    for (const row of metrics.ownerStatusRows) {
      const ownerId = row.ownerId || "unassigned";
      if (!ownerNames.has(ownerId)) {
        ownerNames.set(ownerId, row.ownerName);
      }
      if (!ownerCounts.has(ownerId)) {
        ownerCounts.set(ownerId, 0);
      }
    }

    const teamFilters = Array.from(ownerNames.entries())
      .map(([id, name]) => ({
        id,
        name,
        count: ownerCounts.get(id) || 0,
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    const allCount = teamFilters.reduce((acc, current) => acc + current.count, 0);

    return [
      { id: "all", name: "All", count: allCount },
      ...teamFilters,
    ];
  }, [metrics]);

  const filteredColumns = useMemo(() => {
    if (!metrics) return [];
    const search = searchTerm.trim().toLowerCase();

    return metrics.boardColumns.map((column) => ({
      ...column,
      items: column.items.filter((item) => {
        const ownerId = item.ownerId || "unassigned";
        const ownerMatch = selectedOwner === "all" || ownerId === selectedOwner;
        if (!ownerMatch) {
          return false;
        }

        if (!search) {
          return true;
        }

        const candidate = [
          item.caseNumber,
          item.title || "",
          item.clientName || "",
          item.ownerName || "",
        ]
          .join(" ")
          .toLowerCase();

        return candidate.includes(search);
      }),
    }));
  }, [metrics, searchTerm, selectedOwner]);

  const boardEmpty = useMemo(
    () => filteredColumns.every((column) => column.items.length === 0),
    [filteredColumns],
  );

  const ownerLabel = (ownerId?: string | null, ownerName?: string | null) => {
    if (ownerName && ownerName.trim()) return ownerName;
    if (!ownerId) return "Unassigned";
    return employeesById.get(ownerId)?.full_name || "Unassigned";
  };

  const ownerPhoto = (ownerId?: string | null) => {
    if (!ownerId) return undefined;
    return employeesById.get(ownerId)?.profile_photo || undefined;
  };

  const priorityBadge = (priority?: string | null) => {
    const key = (priority || "medium").toLowerCase();
    const variant = key === "high" ? "destructive" : key === "low" ? "outline" : "secondary";
    return <Badge variant={variant}>{key.toUpperCase()}</Badge>;
  };

  const handlePreviewProposal = async (event: ReactMouseEvent, proposalId: string) => {
    event.stopPropagation();
    if (previewingProposalId) return;
    setPreviewingProposalId(proposalId);
    try {
      const link = await getOrCreateProposalPublicLink(proposalId, "adjusted");
      window.open(`${window.location.origin}/proposal/share/${link.token}`, "_blank");
    } catch (error: any) {
      toast({
        title: "Preview unavailable",
        description: error?.message || "Unable to generate a preview link.",
        variant: "destructive",
      });
    } finally {
      setPreviewingProposalId(null);
    }
  };

  const PreviewProposalButton = ({ proposalId }: { proposalId: string }) => (
    <button
      type="button"
      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      onClick={(event) => { void handlePreviewProposal(event, proposalId); }}
      disabled={previewingProposalId === proposalId}
      title="Preview proposal"
    >
      {previewingProposalId === proposalId ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <ExternalLink className="h-3.5 w-3.5" />
      )}
    </button>
  );

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTripStartDate = (value?: string | null) => {
    if (!value) return "—";
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      const localDate = new Date(year, month - 1, day);
      if (!Number.isNaN(localDate.getTime())) {
        return localDate.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    }
    return formatDate(value);
  };

  const formatBusinessDays = (value?: number | null) => {
    if (value === null || value === undefined) return "—";
    if (value <= 0) return "Today";
    return `${value} bd`;
  };

  const renderOwnerIdentity = (ownerId?: string | null, ownerName?: string | null) => {
    const displayOwnerName = ownerLabel(ownerId, ownerName);
    const color = getUserColorToken(ownerId || displayOwnerName);

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5" style={{ borderColor: color.border }}>
        <Avatar className="h-5 w-5 border" style={{ borderColor: color.border }}>
          <AvatarImage src={ownerPhoto(ownerId)} alt={displayOwnerName} />
          <AvatarFallback
            className="text-[9px] font-semibold"
            style={{ backgroundColor: color.bg, color: color.text }}
          >
            {getUserInitials(displayOwnerName)}
          </AvatarFallback>
        </Avatar>
        <span className="text-[11px] text-muted-foreground">{displayOwnerName}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">General Manager Dashboard</h2>
          <p className="text-muted-foreground">Loading operational insights...</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">General Manager Dashboard</h2>
        <p className="text-muted-foreground">Board view for all proposals, with per-person focus.</p>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Operational Volume</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Proposals Today</CardTitle>
              <Calendar className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.todayProposals}</div>
              <p className="text-xs text-muted-foreground">Proposals created today</p>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            onClick={scrollToUpcomingTrips}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                scrollToUpcomingTrips();
              }
            }}
            className="cursor-pointer transition-colors hover:bg-muted/40"
            title="View which trips are starting"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Next 7 Days</CardTitle>
              <Clock className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.next7Days}</div>
              <p className="text-xs text-muted-foreground">Trips starting · click to view</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <CardTitle>Team Proposal Board</CardTitle>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search board"
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {ownerFilters.map((owner) => {
                const ownerEmployee = owner.id === "all" ? undefined : employeesById.get(owner.id);
                const selected = selectedOwner === owner.id;
                const color = getUserColorToken(owner.id === "all" ? owner.name : owner.id);
                return (
                  <button
                    key={owner.id}
                    type="button"
                    onClick={() => setSelectedOwner(owner.id)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 transition-colors",
                      selected ? "shadow-sm" : "hover:bg-muted/50",
                    )}
                    style={{
                      borderColor: color.border,
                      backgroundColor: selected ? color.bg : undefined,
                      color: selected ? color.text : undefined,
                    }}
                  >
                    <Avatar className="h-7 w-7 border" style={{ borderColor: color.border }}>
                      <AvatarImage src={ownerEmployee?.profile_photo || undefined} alt={owner.name} />
                      <AvatarFallback
                        className="text-[10px] font-semibold"
                        style={{ backgroundColor: color.bg, color: color.text }}
                      >
                        {getUserInitials(owner.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium whitespace-nowrap">{owner.name}</span>
                    <Badge
                      variant="secondary"
                      className="h-5 px-1.5 text-[10px] border"
                      style={{ backgroundColor: color.badgeBg, color: color.badgeText, borderColor: color.border }}
                    >
                      {owner.count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="rounded-xl border border-border bg-card/30 p-4">
            <div className="flex gap-4 overflow-x-auto pb-2">
              {filteredColumns.map((column, index) => (
                <div key={column.status} className="min-w-[280px] max-w-[280px] relative">
                  {index !== 0 && <div className="absolute -left-2 top-0 h-full w-px bg-border/60" />}

                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {column.label}
                    </div>
                    <Badge variant="outline">{column.items.length}</Badge>
                  </div>

                  <div className="rounded-lg border border-dashed border-border/70 bg-background/40 p-2">
                  <ScrollArea className="h-[900px]">
                    <div className="space-y-2 pr-2">
                    {column.items.length === 0 && (
                      <div className="text-xs text-muted-foreground px-1 py-2">No items</div>
                    )}

                    {column.items.map((item) => {
                      const displayOwnerName = ownerLabel(item.ownerId, item.ownerName);
                      const ownerIdFilter = item.ownerId || "unassigned";
                      const ownerColor = getUserColorToken(item.ownerId || displayOwnerName);
                      const isOwnerSelected = selectedOwner === ownerIdFilter;
                      const statusAgeLabel = formatBusinessDays(item.statusAgeBusinessDays);
                      const proposalValue = typeof item.totalValue === "number"
                        ? `$${item.totalValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—";
                      return (
                        <div
                          key={item.proposalId}
                          className={`rounded-md border bg-card p-3 shadow-sm transition-colors cursor-pointer ${
                            item.staleFollowUp
                              ? "border-red-500 ring-2 ring-red-500/60 bg-red-50/60 hover:bg-red-50"
                              : "border-border hover:bg-muted/50"
                          }`}
                          onClick={() => navigate(`/proposals/${item.proposalId}`)}
                        >
                          {item.staleFollowUp && (
                            <div className="mb-2 flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm animate-pulse">
                              <AlertTriangle className="h-3 w-3" />
                              No follow-up {item.daysSinceLastFollowUp ? `${item.daysSinceLastFollowUp}d` : "5d+"}
                            </div>
                          )}
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium">{item.caseNumber}</div>
                            <div className="flex items-center gap-1">
                              <PreviewProposalButton proposalId={item.proposalId} />
                              {priorityBadge(item.priority)}
                            </div>
                          </div>

                          <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            {item.title || item.clientName || "Untitled"}
                          </div>

                          <div className="mt-2 text-[11px] text-muted-foreground flex justify-between gap-2">
                            <span className="truncate">Updated: {formatDate(item.updatedAt)}</span>
                            <span className="truncate">Due: {formatDate(item.expiryDate)}</span>
                          </div>

                          <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                              <span className="inline-flex items-center rounded-full border border-border/70 px-2 py-0.5 text-muted-foreground">
                                {statusAgeLabel}
                              </span>
                              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50/70 px-2 py-0.5 text-emerald-800">
                                {proposalValue}
                              </span>
                              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50/70 px-2 py-0.5 text-blue-800">
                                Start: {formatTripStartDate(item.tripStartDate)}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedOwner(ownerIdFilter);
                              }}
                              title={`Filter by ${displayOwnerName}`}
                              style={isOwnerSelected ? { boxShadow: `0 0 0 2px ${ownerColor.border}` } : undefined}
                            >
                              <Avatar className="h-7 w-7 border" style={{ borderColor: ownerColor.border }}>
                                <AvatarImage src={ownerPhoto(item.ownerId)} alt={displayOwnerName} />
                                <AvatarFallback
                                  className="text-[10px] font-semibold"
                                  style={{ backgroundColor: ownerColor.bg, color: ownerColor.text }}
                                >
                                  {getUserInitials(displayOwnerName)}
                                </AvatarFallback>
                              </Avatar>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </ScrollArea>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {boardEmpty && (
            <div className="text-sm text-muted-foreground mt-4">
              No proposals for the selected filters.
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="text-lg font-semibold mb-3">Internal SLAs</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Creation → Sent</CardTitle>
              <Clock className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.avgCreationToSent.toFixed(1)}h</div>
              <p className="text-xs text-muted-foreground">Average time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sent → Approval</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.avgSentToApproved.toFixed(1)}h</div>
              <p className="text-xs text-muted-foreground">Average time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance SLA</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.slaCompliance.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">Sent within 24h</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Errors and Rework</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue Proposals</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.overdueProposals}</div>
              <p className="text-xs text-muted-foreground">Expiry date before today and not converted</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rework</CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.rework}</div>
              <p className="text-xs text-muted-foreground">Current status starts with needs_fix</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Proposals Pending Completion</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.pendingProposals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending proposals</p>
          ) : (
            <div className="space-y-3">
              {metrics.pendingProposals.map((proposal) => (
                <div
                  key={proposal.id}
                  className="flex items-center justify-between rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/proposals/${proposal.id}`)}
                >
                  <div className="space-y-1">
                    <p className="font-medium">{proposal.internal_reference || "N/A"}</p>
                    <p className="text-sm text-muted-foreground">
                      {proposal.clients?.company || `${proposal.clients?.first_name} ${proposal.clients?.last_name}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Owner: {renderOwnerIdentity(proposal.owner_id, employeesById.get(proposal.owner_id)?.full_name)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ProposalStatusBadge status={proposal.status} size="sm" />
                    <span className="text-sm text-muted-foreground">Step {proposal.last_step}/11</span>
                    <PreviewProposalButton proposalId={proposal.id} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card
        ref={upcomingTripsRef}
        className={cn(
          "scroll-mt-4 transition-shadow",
          highlightUpcomingTrips && "ring-2 ring-primary shadow-md"
        )}
      >
        <CardHeader>
          <CardTitle>Upcoming Trips (7 days)</CardTitle>
          {metrics.upcomingProposals.length > 0 && metrics.upcomingProposals.length < metrics.next7Days && (
            <p className="text-xs text-muted-foreground">
              Showing {metrics.upcomingProposals.length} of {metrics.next7Days}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {metrics.upcomingProposals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No trips in the next 7 days</p>
          ) : (
            <div className="space-y-3">
              {metrics.upcomingProposals.map((proposal) => (
                <div
                  key={proposal.id}
                  className="flex items-center justify-between rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/proposals/${proposal.id}`)}
                >
                  <div className="space-y-1">
                    <p className="font-medium">{proposal.internal_reference || "N/A"}</p>
                    <p className="text-sm text-muted-foreground">
                      {proposal.clients?.company || `${proposal.clients?.first_name} ${proposal.clients?.last_name}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Owner: {renderOwnerIdentity(proposal.owner_id, employeesById.get(proposal.owner_id)?.full_name)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ProposalStatusBadge status={proposal.status} size="sm" />
                    {proposal.proposal_trip_legs?.[0] && (
                      <span className="text-sm text-muted-foreground">
                        {formatDate(proposal.proposal_trip_legs[0].start_date)}
                      </span>
                    )}
                    <PreviewProposalButton proposalId={proposal.id} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GMDashboard;
